import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TENANT_SECRET_SERVICES,
  type AdminTenantSecret,
  type TenantSecretService,
} from '../../../src/shared/admin';
import {
  adminKeys,
  deleteTenantSecret,
  fetchAdminTenant,
  fetchTenantSecrets,
  putTenantSecret,
} from '../../api/admin';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/admin/tenants/:id/secrets` — per-tenant provider API keys (migration 0006
 * `tenant_secrets`). Values are write-only: the list shows only `last4`; each
 * field carries a "create key" quick link to the provider console where the
 * key is minted (`TENANT_SECRET_SERVICES.createUrl`).
 */

export function TenantSecretsPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (id === undefined) {
    // Unreachable via the route table; fail loud rather than render blank.
    throw new Error('TenantSecretsPage rendered without a :id route param');
  }
  return <TenantSecretsPanel tenantId={id} />;
}

function TenantSecretsPanel({ tenantId }: { tenantId: string }): React.JSX.Element {
  const tenant = useQuery({
    queryKey: adminKeys.tenant(tenantId),
    queryFn: ({ signal }) => fetchAdminTenant(tenantId, signal),
  });
  const secrets = useQuery({
    queryKey: adminKeys.tenantSecrets(tenantId),
    queryFn: ({ signal }) => fetchTenantSecrets(tenantId, signal),
  });

  if (tenant.isPending || secrets.isPending) {
    return <LoadingScreen />;
  }
  if (tenant.isError) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <ErrorNotice error={tenant.error} />
      </main>
    );
  }
  if (secrets.isError) {
    return (
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <ErrorNotice error={secrets.error} />
      </main>
    );
  }

  const byService = new Map<TenantSecretService, AdminTenantSecret>(
    secrets.data.secrets.map((secret) => [secret.service, secret])
  );

  return (
    <main className="mx-auto w-full max-w-2xl px-6 py-10">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Secrets — {tenant.data.tenant.name}
        </h1>
        <Link
          to="/admin"
          className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Back to admin
        </Link>
      </div>
      <p className="mt-2 text-slate-600">
        Per-tenant provider API keys, encrypted at rest. Values are write-only — only the last four
        characters are shown after saving.
      </p>

      <div className="mt-6 space-y-4">
        {TENANT_SECRET_SERVICES.map((meta) => (
          <SecretField
            key={meta.service}
            tenantId={tenantId}
            meta={meta}
            stored={byService.get(meta.service) ?? null}
          />
        ))}
      </div>
    </main>
  );
}

function SecretField({
  tenantId,
  meta,
  stored,
}: {
  tenantId: string;
  meta: (typeof TENANT_SECRET_SERVICES)[number];
  stored: AdminTenantSecret | null;
}): React.JSX.Element {
  const [value, setValue] = useState('');
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: adminKeys.tenantSecrets(tenantId) });
  };

  const saveMutation = useMutation({
    mutationFn: () => putTenantSecret(tenantId, meta.service, value),
    onSuccess: () => {
      setValue('');
      invalidate();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteTenantSecret(tenantId, meta.service),
    onSuccess: invalidate,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (value.trim().length < 4) {
      return;
    }
    saveMutation.mutate();
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-base font-medium">{meta.label}</h2>
        <a
          href={meta.createUrl}
          target="_blank"
          rel="noreferrer"
          className="whitespace-nowrap text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Create key at {meta.provider} ↗
        </a>
      </div>

      <p className="mt-1 text-sm text-slate-500">
        {stored === null ? (
          'Not set.'
        ) : (
          <>
            Set — ends in <code className="font-mono">…{stored.last4}</code>, updated{' '}
            {new Date(stored.updatedAt).toLocaleString()}.
          </>
        )}
      </p>

      <form className="mt-3 flex items-start gap-2" onSubmit={handleSubmit} noValidate>
        <input
          aria-label={`${meta.label} value`}
          type="password"
          autoComplete="off"
          placeholder={stored === null ? 'Paste key value' : 'Paste new value to replace'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={value.trim().length < 4 || saveMutation.isPending}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </button>
        {stored !== null && (
          <button
            type="button"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </button>
        )}
      </form>

      {saveMutation.isError && <ErrorNotice error={saveMutation.error} className="mt-3" />}
      {deleteMutation.isError && <ErrorNotice error={deleteMutation.error} className="mt-3" />}
    </section>
  );
}
